/* eslint-disable @typescript-eslint/no-explicit-any */
import { paths } from '@common/types';
import { toNumber } from 'lodash';
import { usePreviewContext } from '../../contexts';
import { IPreviewTableFilters } from '../../../types.ts';
import { useReportStore } from '@store';
import {
  usePatientReportGroupBySectionsQuery,
  useRecordReportGroupBySectionsQuery,
  useTaskReportGroupBySectionsQuery,
  useUserReportGroupBySectionsQuery,
} from '@common/cache';
import { useEffect, useRef, useState } from 'react';
import useSidebarStore from 'store/sideBarStore.store.ts';

export const useGroupsTable = () => {
  const { filters, handleFilterChange } = usePreviewContext();
  const { report } = useReportStore();
  const { isSidebarOpen } = useSidebarStore();
  const tableRef = useRef<HTMLDivElement>(null);

  const [footerWidth, setFooterWidth] = useState<string | number>('100%');
  const [openGroup, setOpenGroup] = useState<string | null>('');

  const handleToggleGroup = (groupName: string | null) => {
    setOpenGroup(prev => (prev === groupName ? '' : groupName));
  };

  useEffect(() => {
    if (tableRef.current) {
      setFooterWidth(tableRef.current.clientWidth);
    }
  }, [isSidebarOpen]);

  const queryParams = {
    search: filters.search,
    limit: filters.limit,
    page: filters.page,
  };

  const patientQuery = usePatientReportGroupBySectionsQuery(
    report?.id ?? '',
    {
      ...queryParams,
      group_by:
        filters.group_by as paths[`/reporting/patients/reports/{report_id}/groups`]['get']['parameters']['query']['group_by'],
      $and: filters?.options?.$and ?? ([] as any),
    },
    report?.report_type === 'patients_report',
  );

  const userQuery = useUserReportGroupBySectionsQuery(
    report?.id ?? '',
    {
      ...queryParams,
      group_by:
        filters.group_by as paths[`/reporting/users/reports/{report_id}/groups`]['get']['parameters']['query']['group_by'],
      $and: filters?.options?.$and ?? ([] as any),
    },
    report?.report_type === 'users_report',
  );
  const recordQuery = useRecordReportGroupBySectionsQuery(
    report?.id ?? '',
    {
      ...queryParams,
      group_by:
        filters.group_by as paths[`/reporting/record/reports/{report_id}/groups`]['get']['parameters']['query']['group_by'],
      $and: filters?.options?.$and ?? ([] as any),
    },
    report?.report_type === 'records_report',
  );
  const taskQuery = useTaskReportGroupBySectionsQuery(
    report?.id ?? '',
    {
      ...queryParams,
      group_by:
        filters.group_by as paths[`/reporting/tasks/reports/{report_id}/groups`]['get']['parameters']['query']['group_by'],
      $and: filters?.options?.$and ?? ([] as any),
    },
    report?.report_type === 'tasks_report',
  );

  const groupsQuery = (() => {
    switch (report?.report_type) {
      case 'patients_report':
        return patientQuery;
      case 'users_report':
        return userQuery;
      case 'records_report':
        return recordQuery;
      case 'tasks_report':
        return taskQuery;
      default:
        return undefined;
    }
  })();

  const { sort_order } = filters;

  const handleChangeOrder = (colName: string) => {
    handleFilterChange({
      ...filters,
      sort_by: colName,
      sort_order: sort_order === 'ASC' ? 'DESC' : 'ASC',
    } as Partial<IPreviewTableFilters>);
  };

  const onPageIndexChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    handleFilterChange({ ...filters, page: page + 1 });
  };

  const onPageSizeChange = (event: { target: { value: string } }) => {
    handleFilterChange({
      ...filters,
      limit: toNumber(event.target.value),
      page: 1,
    });
  };

  return {
    filters,
    groups: groupsQuery?.data?.groups ?? [],
    totalItemsCount: groupsQuery?.data?.total_items_count ?? 0,
    handleFilterChange,
    totalCount: groupsQuery?.data?.total ?? 0,
    loading: groupsQuery?.isLoading ?? false,
    handleChangeOrder,
    onPageIndexChange,
    onPageSizeChange,
    tableRef,
    footerWidth,
    handleToggleGroup,
    openGroup,
  };
};
